import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import AWSConnectForm from "./AWSConnectForm";
import AWS_ROOT_1 from "./images/AWS_ROOT_1.png";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { awsIntegratedKeys, integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import AWS_ROOT_2 from "./images/AWS_ROOT_2.png";
import AWS_ROOT_3 from "./images/AWS_ROOT_3.png";
import PermissionsSummary from "./PermissionSummary";

const AWSRootOrganization = ({ awsRegionOptions, userPermissions }) => {
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [breezeAccountID, setBreezeAccountID] = useState("");
  const [AWSRootAccountIsIntegrated, setAWSRootAccountIsIntegrated] =
    useState(false);
  const [externalId, setExternalId] = useState("");
  const queryClient = useQueryClient();

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const {
    registerAWSIntegration,
    fetchAWSAccountID,
    checkAWSRootAccountIsIntegrated,
    getCloudFormationFolder,
    generateAWSExternalId,
  } = useAPI();
  const colors = useColors();
  const breezeDefaultStackName = "BreezePlatform";

  const { data: CloudFormationFolder } = useQuery({
    queryKey: awsIntegratedKeys.cloudFormationFolder(),
    queryFn: getCloudFormationFolder,
    select: (res) => res.data,
  });

  const quickCreateUrl = (region) => {
    return (
      `https://${region}.console.aws.amazon.com/cloudformation/home?region=${region}#/stacks/quickcreate?` +
      `templateURL=https%3A%2F%2Fbreeze-security-public.s3.eu-west-1.amazonaws.com%2F${CloudFormationFolder}%2FBreezePlatform.json&` +
      "stackName=" +
      breezeDefaultStackName +
      "&" +
      "param_BreezeRoleName=BreezeSecurity-Role&" +
      "param_BreezeAWS=" +
      breezeAccountID +
      "&" +
      "param_ExternalId=" +
      externalId
    );
  };

  const handleLinkClick = () => {
    // Open the IAM console in a new tab with the stack creation URL
    // Filled with the default values
    window.open(quickCreateUrl(selectedRegion?.code || ""), "_blank");
  };

  useEffect(() => {
    Promise.all([fetchAWSAccountID(), generateAWSExternalId()]).then(
      ([accountResp, externalIdResp]) => {
        setBreezeAccountID(accountResp.data.account_id);
        setExternalId(externalIdResp.data.external_id);
      }
    );
  }, []);

  const onSubmit = (values) => {
    setLoading(true);
    registerAWSIntegration(
      values.roleARN.trim(),
      values.region,
      "", // legacy nickname place - not used
      true,
      externalId
    )
      .then((response) => {
        Toast.success("AWS Root account integrated successfully");
        setLoading(false);
        setAWSRootAccountIsIntegrated(true);
        queryClient.invalidateQueries({
          queryKey: awsIntegratedKeys.rootAccount(),
        });
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
          setLoading(false);
        } else {
          Toast.error("An error occurred while processing the request.");
          setLoading(false);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    checkAWSRootAccountIsIntegrated().then((response) => {
      setAWSRootAccountIsIntegrated(response.data);
      setLoading(false);
    });
  }, []);

  const handleRegionChange = (newValue) => {
    if (!newValue) {
      setSelectedRegion(null);
      return;
    }
    setSelectedRegion(newValue);
  };

  if (loading) {
    return <LoadingIcon />;
  }

  return (
    <Box mt={2}>
      {AWSRootAccountIsIntegrated ? (
        <>
          <Typography variant="h5">
            The root account has been integrated.
          </Typography>
          <br />
          <Typography variant="subtitle1">Use:</Typography>
          <Typography variant="subtitle2">
            <strong>Tab 2. All Member Accounts</strong> to add all member
            accounts
          </Typography>
          <Typography variant="subtitle2">
            <strong>Tab 3. Single Member Account</strong> to add more accounts
            one by one
          </Typography>
          <Typography variant="subtitle2">
            <strong>Tab 4. Manage Accounts</strong> to manage the accounts
            (Regions, Nickname, etc.)
          </Typography>
        </>
      ) : (
        <>
          {/* BUTTONS TO CONTROL EXPANSION */}
          <Box mt="20px" mb="20px">
            <Grid container spacing={2} direction={"row"}>
              <Grid item>
                <SecondaryButton onClick={handleExpandAll}>
                  Expand All Screenshots
                </SecondaryButton>
              </Grid>
              <Grid item>
                <SecondaryButton onClick={handleCollapseAll}>
                  Collapse All Screenshots
                </SecondaryButton>
              </Grid>
            </Grid>
          </Box>

          {/* INSTRUCTIONS */}
          <GuidelineStepByStep>
            <GuidelineStep title="Sign in to the Root AWS account"></GuidelineStep>
            <GuidelineStep title="Ensure you have the necessary AWS permissions">
              <Accordion sx={{ width: "700px", backgroundColor: "#f5f5f5" }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  {" "}
                  <Typography variant="subtitle2" color={colors.primary_darker}>
                    AWS required permissions
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {userPermissions?.map((permission, index) => (
                    <Typography key={index}>{permission}</Typography>
                  ))}
                </AccordionDetails>
              </Accordion>
            </GuidelineStep>

            {/* Step for AWS CloudFormation Stack */}
            <GuidelineStep title="Run the AWS CloudFormation Stack to create an IAM Role for Breeze">
              {" "}
              <>
                <Box>
                  <Typography variant={"body2"} mb={2}>
                    Select the AWS region where you want to create the stack.
                  </Typography>
                  <Autocomplete
                    sx={{
                      color: colors.text_primary,
                      borderRadius: "10px",
                      "& .MuiAutocomplete-clearIndicator": {
                        display: "none",
                      },
                    }}
                    id="aws-region-autocomplete"
                    options={awsRegionOptions}
                    getOptionLabel={(option) =>
                      `${option.name} (${option.code})`
                    }
                    value={selectedRegion}
                    onChange={(event, newValue) => handleRegionChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="AWS Region"
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            height: "50px",
                            width: "300px",
                            "& fieldset": {
                              borderColor: colors.divider2,
                            },
                            "&:hover fieldset": {
                              borderColor: colors.divider2,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: colors.divider2,
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: "14px",
                          },
                        }}
                      />
                    )}
                    ListboxProps={{
                      sx: {
                        "& .MuiAutocomplete-option": {
                          fontSize: "14px",
                        },
                      },
                    }}
                    componentsProps={{
                      popupIndicator: {
                        disableRipple: true,
                      },
                    }}
                  />
                </Box>
                <PrimaryButton
                  sx={{
                    height: "36px",
                    borderRadius: "10px",
                    width: "260px",
                    textTransform: "none",
                  }}
                  onClick={handleLinkClick}
                  disabled={!selectedRegion}
                >
                  Deploy CloudFormation Stack
                </PrimaryButton>
                <PermissionsSummary />
              </>
            </GuidelineStep>

            {/* Step for External ID */}
            <GuidelineStep title="Verify the External ID">
              <Typography variant={"body2"} mb={2}>
                For security purposes, make sure the External ID shown below
                matches exactly with the one in your CloudFormation stack:
              </Typography>
              <Box
                sx={{
                  backgroundColor: colors.background_darker,
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2" color={colors.text_primary}>
                  {externalId}
                </Typography>
              </Box>
              <Typography variant={"body2"} color={colors.warning_darker}>
                Important: Do not modify this value. If the External ID doesn't
                match, the integration will fail. This is a security measure to
                ensure secure cross-account access.
              </Typography>
            </GuidelineStep>

            <GuidelineStep title="Edit the values if you wish and click Next">
              <Typography variant={"body2"}>
                Stack name: <strong>{breezeDefaultStackName}</strong> (default)
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant={"body2"}>
                  BreezeAWS: <strong>{breezeAccountID}</strong>
                </Typography>{" "}
                <CustomCopyButton
                  copyTitle={"AWS Account ID"}
                  copyText={breezeAccountID}
                />
              </Box>
              <Typography variant={"body2"}>
                BreezeRoleName: Name of the role that will be created for Breeze
                to assume (default: <strong>BreezeSecurity-Role</strong>)
              </Typography>
              <ScreenshotAccordion
                images={[
                  {
                    file: AWS_ROOT_1,
                    altext: "AWS CloudFormation Creation",
                  },
                ]}
                onExpanded={addToExpandedList}
              />
            </GuidelineStep>
            <GuidelineStep title="Down below on Capabilities section accept the terms">
              <ScreenshotAccordion
                images={[
                  {
                    file: AWS_ROOT_2,
                    altext: "AWS CloudFormation Creation",
                  },
                ]}
                onExpanded={addToExpandedList}
              />
            </GuidelineStep>
            <GuidelineStep title="Click Create Stack"></GuidelineStep>
            <GuidelineStep title="Wait for the stack to be created and copy the Role ARN from output"></GuidelineStep>
            <GuidelineStep title="Fill the required values in the form below and click Connect">
              <ScreenshotAccordion
                images={[
                  {
                    file: AWS_ROOT_3,
                    altext: "AWS Role ARN",
                  },
                ]}
                onExpanded={addToExpandedList}
              />
              <AWSConnectForm
                onSubmit={onSubmit}
                awsRegions={awsRegionOptions}
              />
            </GuidelineStep>
          </GuidelineStepByStep>
        </>
      )}
    </Box>
  );
};

export default AWSRootOrganization;
